/*
 * The one place the console asks the browser how wide it is.
 *
 * Nearly all of the responsive contract is CSS — grids collapse, the sidebar becomes a drawer, the
 * save bar sticks. This hook exists for the one case CSS cannot do: a table and a list of record
 * cards are different DOM, not the same DOM at a different width, and rendering both and hiding one
 * doubles every row in the accessibility tree for the sake of a media query.
 *
 * `matches` starts false so the desktop layout is what renders on the server, in a test, and in the
 * first frame — the table is the richer of the two, and briefly showing it is better than briefly
 * showing a stripped-down card list to someone on a desktop.
 */
import { useEffect, useState } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    setMatches(list.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Safari below 14 has only the deprecated pair, and venue staff are on the tablets they have.
    if (list.addEventListener) list.addEventListener('change', onChange);
    else list.addListener(onChange);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', onChange);
      else list.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

/** The phone breakpoint the console's CSS already uses. One decision at a time below this. */
export const PHONE_QUERY = '(max-width: 767px)';

export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}
